// Shared helpers, constants, and mini-components for the Kasse → Kontrolle module.
// Extracted from KontrollePage.tsx so that KontrolleDrawer and BarcodeScanModal
// can import them without circular deps.
import { useState, useMemo, type ReactNode } from 'react'
import type { KbStatus, KbOfficeFields } from '../kassenbuchApi'

export { eur, eurSigned, isoToday, formatDateDE } from '../office'
import { sourcesOf, counted, hasCoffee, eur, formatDateDE } from '../office'
import { Badge } from '../../../shared/ui'
import { useTranslation } from 'react-i18next'

export function SourceBadge({ record }: { record: KbOfficeFields }) {
  const { t } = useTranslation('kasse')
  return <span className="inline-flex gap-1">{sourcesOf(record).map(source => <Badge key={source} size="sm" tone="neutral">{t(`office.source.${source}`)}</Badge>)}</span>
}

export function CoffeeValue({ record }: { record: KbOfficeFields }) {
  const { t } = useTranslation('kasse')
  if (!hasCoffee(record)) return null
  return <>{eur(record.kaffee)}{record.kaffee != null && <span className="block text-[10px] font-normal text-on-surface-variant">{t(record.kaffeeImGezaehlten === true ? 'office.included' : record.kaffeeImGezaehlten === false ? 'office.addedShort' : 'office.unknown')}</span>}</>
}

/** The detail wraps within the counted column instead of setting its minimum width. */
export function MoneyValue({ record }: { record: KbOfficeFields & { umsatz?: string | null } }) {
  const { t } = useTranslation('kasse')
  return <div className="text-right tabular-nums">
    <div className="font-bold text-on-surface whitespace-nowrap">{eur(counted(record))}</div>
    {hasCoffee(record) && <div className="mt-0.5 w-0 min-w-full text-[10px] leading-snug font-normal text-on-surface-variant">
      {t(record.kaffeeImGezaehlten === true ? 'office.coffeeIncludedSummary'
        : record.kaffeeImGezaehlten === false ? 'office.coffeeAddedSummary' : 'office.coffeeSummary', {
        coffee: eur(record.kaffee), total: eur(record.gesamtumsatz),
      })}
    </div>}
  </div>
}


export function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + n)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// ── week helpers (KW selector + per-school week view) ────────────────────
export function weekMonday(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  const dow = d.getUTCDay()                       // 0=Sun..6=Sat
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow))
  return d.toISOString().slice(0, 10)
}

export function isoWeekNum(iso: string): number {
  const d = new Date(iso + 'T00:00:00Z')
  const day = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - day + 3)          // Thursday of this ISO week
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4))
  const ftDay = (firstThu.getUTCDay() + 6) % 7
  firstThu.setUTCDate(firstThu.getUTCDate() - ftDay + 3)
  return 1 + Math.round((d.getTime() - firstThu.getTime()) / (7 * 864e5))
}

export function weekdays(monday: string): string[] {
  return [0, 1, 2, 3, 4].map((n) => addDays(monday, n))   // Mo–Fr (school canteens don't run weekends)
}

export function weekLabel(monday: string): string {
  const fri = addDays(monday, 4)
  return `KW ${isoWeekNum(monday)} · ${formatDateDE(monday)}–${formatDateDE(fri)}`
}

export function recentWeeks(anchorIso: string, n = 12): { value: string; label: string }[] {
  const mon0 = weekMonday(anchorIso)
  return Array.from({ length: n }, (_, i) => {
    const m = addDays(mon0, -7 * i)
    return { value: m, label: weekLabel(m) }
  })
}

export const DOW_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr'] as const

export const SPRING = { type: 'spring', stiffness: 240, damping: 28, mass: 0.9 } as const

// ── Denomination visual: colour-accurate EUR note / coin chip (fast recognition) ──
export const NOTE_COLOR: Record<number, string> = {
  500: '#7a5fa6', 200: '#b8923a', 100: '#4a9e6a', 50: '#e2911f', 20: '#3f7cb3', 10: '#c0504d', 5: '#9aa0a6',
}

export function DenomChip({ value, kind }: { value: number; kind: 'note' | 'coin' }) {
  if (kind === 'note') {
    const bg = NOTE_COLOR[value] ?? '#9aa0a6'
    return (
      <span
        className="inline-flex items-center justify-center w-14 h-7 rounded-[4px] text-[11px] font-extrabold text-white shadow-sm ring-1 ring-black/10 tabular-nums"
        style={{ background: `linear-gradient(135deg, ${bg}, ${bg}cc)` }}
        title={`${value} € Schein`}
      >
        {value}€
      </span>
    )
  }
  const tone =
    value >= 1 ? 'radial-gradient(circle at 35% 30%, #f4e7b5, #c9a227 70%)'      // 1 € / 2 € (gold)
    : value >= 0.1 ? 'radial-gradient(circle at 35% 30%, #f3e2a0, #c8a02a 72%)'  // 10/20/50 ct (nordic gold)
    : 'radial-gradient(circle at 35% 30%, #e8a877, #a85a2a 72%)'                 // 1/2/5 ct (copper)
  const label = value >= 1 ? `${value}€` : `${Math.round(value * 100)}c`
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[9px] font-extrabold text-black/70 shadow-sm ring-1 ring-black/15 tabular-nums"
      style={{ background: tone }}
      title={`${label} Münze`}
    >
      {label}
    </span>
  )
}

// ── filter types + sync formatter ──────────────────────────────────────
export type FilterKey = 'all' | 'flagged' | 'open'
export type ControlKey = 'all' | 'yes' | 'no'
export const fmtSync = (ms?: number) => (ms ? new Date(ms).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin' }) : '—')

export const FILTER_OPTIONS: readonly { value: FilterKey; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'flagged', label: 'Abweichungen' },
  { value: 'open', label: 'Offen' },
]

// ── tone helpers ────────────────────────────────────────────────────────
export function statusTone(s: KbStatus): 'success' | 'warn' | 'danger' | 'neutral' {
  if (s === 'match') return 'success'
  if (s === 'minor') return 'warn'
  if (s === 'flagged') return 'danger'
  return 'neutral'
}

export function statusLabel(s: KbStatus): string {
  if (s === 'match') return 'Stimmt überein'
  if (s === 'minor') return 'Kleine Differenz'
  if (s === 'flagged') return 'Abweichung'
  if (s === 'no_kassenbuch') return 'Kein Kassenbuch'
  if (s === 'no_data') return 'Keine Daten'
  return 'Kein SAP'
}

export function diffCellClass(s: KbStatus): string {
  if (s === 'match') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  if (s === 'minor') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
  if (s === 'flagged') return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
  return 'bg-surface-container text-on-surface-variant'
}

export function reconBg(s: KbStatus): string {
  if (s === 'match') return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/40'
  if (s === 'minor') return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/40'
  if (s === 'flagged') return 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/40'
  return 'bg-surface-container border-outline-variant/30'
}

export function reconDiffClass(s: KbStatus): string {
  if (s === 'match') return 'text-emerald-700 dark:text-emerald-300'
  if (s === 'minor') return 'text-amber-700 dark:text-amber-300'
  if (s === 'flagged') return 'text-rose-700 dark:text-rose-300'
  return 'text-on-surface-variant'
}

// ── sort helper ─────────────────────────────────────────────────────────
type SortDir = 'asc' | 'desc'

export function useSorter<T>(rows: T[], initialKey: keyof T, initialDir: SortDir = 'desc') {
  const [key, setKey] = useState<keyof T>(initialKey)
  const [dir, setDir] = useState<SortDir>(initialDir)
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key]
      const an = Number(av), bn = Number(bv)
      const cmp =
        Number.isFinite(an) && Number.isFinite(bn)
          ? an - bn
          : String(av ?? '').localeCompare(String(bv ?? ''))
      return dir === 'asc' ? cmp : -cmp
    })
  }, [rows, key, dir])
  const toggle = (k: keyof T) => {
    if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setKey(k); setDir('desc') }
  }
  return { sorted, key, dir, toggle }
}

export function SortTh<T>({
  label, col, sort, align = 'left', className = '',
}: {
  label: string
  col: keyof T
  sort: { key: keyof T; dir: SortDir; toggle: (k: keyof T) => void }
  align?: 'left' | 'right'
  className?: string
}): ReactNode {
  const active = sort.key === col
  return (
    <th
      className={`px-3 py-2.5 cursor-pointer select-none whitespace-nowrap hover:text-on-surface
        ${align === 'right' ? 'text-right' : 'text-left'}
        ${active ? 'text-on-surface' : ''}
        ${className}`}
      onClick={() => sort.toggle(col)}
    >
      {label}{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  )
}

// Zero-centred diverging bar — rose left = negative (Fehlbetrag / zu wenig),
// emerald right = positive (Überschuss / mehr). Scaled to the view's max |value|.
export function DivergeBar({ value, max, className = 'w-24' }: { value: number; max: number; className?: string }) {
  const pct = max > 0 ? Math.min(100, (Math.abs(value) / max) * 100) : 0
  const neg = value < -0.005
  const pos = value > 0.005
  return (
    <span className={`inline-flex items-center h-2.5 flex-none ${className}`} aria-hidden="true">
      <span className="flex-1 flex justify-end">
        {neg && <span className="h-1.5 rounded-l-full bg-rose-400/80" style={{ width: `${pct}%` }} />}
      </span>
      <span className="w-px h-2.5 bg-outline-variant/60 flex-none" />
      <span className="flex-1 flex">
        {pos && <span className="h-1.5 rounded-r-full bg-emerald-400/80" style={{ width: `${pct}%` }} />}
      </span>
    </span>
  )
}

export const THEAD_CLS = 'sticky top-0 bg-surface-container-low z-10'
export const THEAD_ROW_CLS = 'text-[10px] uppercase tracking-widest text-on-surface-variant border-b border-outline-variant/20'
